import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type AssignJourneyInput = {
  journeyId: string;
  partnerIds: string[];
};

export type AssignJourneyResponse = { created: number };

// POST /api/admin/journeys/:journeyId/assignments
//
// Replaces /app/admin/journeys/_actions.ts::assignJourneyToPartnersAction.
// Pessimistic — assignment is a cross-entity state-machine transition that
// spawns journey_task_progress rows via the materialise trigger; the brief
// reaffirmed pessimism on state-machine flips (Session 10). Bulk by default
// — the legacy action always took an array. `created` may be < partnerIds.length
// when some partners are already assigned (service skips them).
export function useAssignJourney() {
  const queryClient = useQueryClient();
  return useMutation<AssignJourneyResponse, ApiClientError, AssignJourneyInput>({
    mutationFn: ({ journeyId, partnerIds }) =>
      apiFetch<AssignJourneyResponse>(
        `/admin/journeys/${journeyId}/assignments`,
        {
          method: "POST",
          body: JSON.stringify({ partnerIds }),
        },
      ),
    onSuccess: (_data, { journeyId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.journeys.list() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(journeyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to assign journey");
    },
  });
}
